import type { Feature, FeatureCollection, Polygon } from 'geojson';

import { booleanDisjoint, kinks } from '@turf/turf';

import useTranslation from './hooks/useTranslation';

import { MINIMUM_POLYGON_POINTS } from './constants';

const useValidateDrawing = () => {
    const { IS_MINIMUM_POINTS, OVERLAPPING, SELF_INTERSECTING } = useTranslation();

    const isSelfIntersecting = (feature: Feature<Polygon>): boolean =>
        kinks(feature).features.length > 0;

    const isOverlapping = (feature: Feature<Polygon>, featureCollection?: FeatureCollection) => {
        if (!featureCollection) {
            return false;
        }

        const { features } = featureCollection;

        return features.some(existingFeature => {
            if (existingFeature === feature) {
                return false;
            }

            if (
                existingFeature.id !== undefined &&
                feature.id !== undefined &&
                existingFeature.id === feature.id
            ) {
                return false;
            }

            return !booleanDisjoint(feature, existingFeature);
        });
    };

    const isMinimumPoints = (feature: Feature<Polygon>): boolean =>
        feature.geometry.coordinates[0].length < MINIMUM_POLYGON_POINTS;

    const boundaryVerifications = [
        {
            errorMessage: SELF_INTERSECTING,
            validate: isSelfIntersecting
        },
        {
            errorMessage: OVERLAPPING,
            validate: isOverlapping
        },
        {
            errorMessage: IS_MINIMUM_POINTS,
            validate: isMinimumPoints
        }
    ];

    const obstacleVerifications = [
        {
            errorMessage: SELF_INTERSECTING,
            validate: isSelfIntersecting
        },
        {
            errorMessage: IS_MINIMUM_POINTS,
            validate: isMinimumPoints
        }
    ];

    const validateBoundaryPolygon = (feature: Feature<Polygon>, features?: FeatureCollection) =>
        boundaryVerifications
            .filter(validation => validation.validate(feature, features))
            .map(validation => new Error(validation.errorMessage));

    const validateObstaclePolygon = (feature: Feature<Polygon>) =>
        obstacleVerifications
            .filter(validation => validation.validate(feature))
            .map(validation => new Error(validation.errorMessage));

    return {
        validateBoundaryPolygon,
        validateObstaclePolygon
    };
};

export default useValidateDrawing;
